import { Component, createSignal, onMount, createMemo, Show } from 'solid-js';
import { useNavigate, useLocation } from '@solidjs/router';
import LineChart from '../components/charts/LineChart';
import DataTable from '../components/tables/DataTable';
import DateRangePicker from '../components/ui/DateRangePicker';
import StatsCard from '../components/ui/StatsCard';
import { TbClick, TbCheckupList, TbCoin, TbArrowLeft } from 'solid-icons/tb';
import { statsApi } from '../services/api';
import { getTodayGMT } from '../utils/timezone';
import moment from 'moment';

export interface UserReportRow {
  date: string;
  visits: number;
  leads: number;
  cr: number;
  epc: number;
  earning: number;
}

const UserReport: Component = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Extract userId from pathname: /report/user/{userId}
  const userId = () => {
    const match = location.pathname.match(/\/report\/user\/(.+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const [loading, setLoading] = createSignal(true);
  const today = getTodayGMT();
  // Default to last 7 days
  const sevenDaysAgo = moment(today).subtract(6, 'days').format('YYYY-MM-DD');
  const [startDate, setStartDate] = createSignal(sevenDaysAgo);
  const [endDate, setEndDate] = createSignal(today);
  const [reportData, setReportData] = createSignal<UserReportRow[]>([]);

  // Compute stats from table data
  const totalClicks = createMemo(() => {
    const data = reportData();
    if (data.length === 0) return 0;
    return data.reduce((a, b) => a + b.visits, 0);
  });

  const totalConversions = createMemo(() => {
    const data = reportData();
    if (data.length === 0) return 0;
    return data.reduce((a, b) => a + b.leads, 0);
  });

  const totalEarnings = createMemo(() => {
    const data = reportData();
    if (data.length === 0) return 0;
    return data.reduce((a, b) => a + b.earning, 0);
  });

  // Check if date range spans 2 or more different days
  const isMultiDayRange = createMemo(() => {
    const start = moment(startDate());
    const end = moment(endDate());
    return end.diff(start, 'days') >= 1;
  });

  // Prepare chart data from report data
  const chartData = createMemo(() => {
    const data = reportData();
    const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));
    return {
      labels: sorted.map(d => d.date),
      clicks: sorted.map(d => d.visits),
      conversions: sorted.map(d => d.leads),
      earnings: sorted.map(d => d.earning),
    };
  });

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    loadData();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await statsApi.getUserReport(userId(), startDate(), endDate());

      if (data && data.length > 0) {
        const mappedData = data.map((item: any) => ({
          date: item.date,
          visits: item.clicks,
          leads: item.conversions,
          cr: parseFloat(item.cr) || 0,
          epc: parseFloat(item.epc) || 0,
          earning: parseFloat(item.earning) || 0,
        }));
        setReportData(mappedData);
      } else {
        setReportData([]);
      }
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/report');
  };

  onMount(() => {
    loadData();
  });

  return (
    <div class="space-y-6">
      {/* Header */}
      <div class="flex items-center gap-4">
        <button
          onClick={handleBack}
          class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Back to Report"
        >
          <TbArrowLeft size={24} class="text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
            User Report: {userId()}
          </h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Daily performance breakdown for this user
          </p>
        </div>
      </div>

      {/* Stats */}
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatsCard
          title="Total Clicks"
          value={totalClicks().toString()}
          icon={() => <TbClick size={24} />}
          color="blue"
          loading={loading()}
        />
        <StatsCard
          title="Total Conversions"
          value={totalConversions().toString()}
          icon={() => <TbCheckupList size={24} />}
          color="green"
          loading={loading()}
        />
        <StatsCard
          title="Total Earning"
          value={`$${totalEarnings().toFixed(2)}`}
          icon={() => <TbCoin size={24} />}
          color="yellow"
          loading={loading()}
        />
      </div>

      {/* Date Filter */}
      <DateRangePicker
        onDateChange={handleDateChange}
        initialStartDate={startDate()}
        initialEndDate={endDate()}
      />

      {/* Charts */}
      <Show when={isMultiDayRange() && reportData().length > 1}>
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Performance Overview
          </h2>
          <LineChart
            labels={chartData().labels}
            datasets={[
              {
                label: 'Clicks',
                data: chartData().clicks,
                borderColor: 'rgb(99, 102, 241)',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                yAxisID: 'y',
              },
              {
                label: 'Conversions',
                data: chartData().conversions,
                borderColor: 'rgb(16, 185, 129)',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                yAxisID: 'y1',
              },
              {
                label: 'Earning',
                data: chartData().earnings,
                borderColor: 'rgb(245, 158, 11)',
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                yAxisID: 'y2',
              },
            ]}
            height={300}
          />
        </div>
      </Show>

      {/* Daily Performance Table */}
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div class="p-6 border-b border-dashed border-gray-200 dark:border-gray-700">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Daily Performance
          </h2>
        </div>
        <DataTable<UserReportRow>
          data={reportData()}
          columns={[
            {
              key: 'date',
              label: 'Date',
              sortable: true,
              render: (item) => moment(item.date).format('dddd, D MMM YYYY'),
            },
            {
              key: 'visits',
              label: 'Visits',
              sortable: true,
              render: (item) => item.visits.toLocaleString(),
            },
            {
              key: 'leads',
              label: 'Leads',
              sortable: true,
              render: (item) => item.leads.toLocaleString(),
            },
            {
              key: 'cr',
              label: 'CR',
              sortable: true,
              render: (item) => `${item.cr.toFixed(2)} %`,
            },
            {
              key: 'epc',
              label: 'EPC',
              sortable: true,
              render: (item) => `${item.epc.toFixed(2)}`,
            },
            {
              key: 'earning',
              label: 'Earning',
              sortable: true,
              render: (item) => (
                <div class="flex items-center gap-1">
                  <span>{item.earning.toFixed(2)}</span>
                  <span class="text-orange-500">💰</span>
                </div>
              ),
            },
          ]}
          loading={loading()}
          searchable={true}
          searchPlaceholder="Search by Date..."
        />
      </div>
    </div>
  );
};

export default UserReport;
